import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import { useU2000AuthContext } from "../auth/U2000UseAuthContext";
import { getWebSocketUrl } from "../../tools/U2000WebSocketUtils";
import type { U2000WebSocketMessage } from "../../types/U2000WebSocketMessages";

/**
 * Type pour les listeners de messages WebSocket
 */
type MessageListener = (message: U2000WebSocketMessage) => void;

/**
 * Type pour les listeners par type de message
 */
type MessageListeners = {
  [K in U2000WebSocketMessage["type"]]?: Set<MessageListener>;
};

export interface U2000WebSocketContextType {
  /**
   * État de la connexion WebSocket
   */
  isConnected: boolean;
  /**
   * S'abonner à un type de message
   * @param type Type de message (ex: 'job:completed')
   * @param listener Fonction appelée lors de la réception du message
   * @returns Fonction pour se désabonner
   */
  on: <T extends U2000WebSocketMessage["type"]>(type: T, listener: (message: Extract<U2000WebSocketMessage, { type: T }>) => void) => () => void;
  /**
   * Se désabonner d'un type de message
   * @param type Type de message
   * @param listener Fonction à retirer
   */
  off: <T extends U2000WebSocketMessage["type"]>(type: T, listener: (message: Extract<U2000WebSocketMessage, { type: T }>) => void) => void;
}

const U2000WebSocketContext = createContext<U2000WebSocketContextType | undefined>(undefined);

export function useU2000WebSocketContext(): U2000WebSocketContextType {
  const context = useContext(U2000WebSocketContext);
  if (!context) {
    throw new Error("useU2000WebSocketContext must be used within U2000WebSocketProvider");
  }
  return context;
}

interface U2000WebSocketProviderProps {
  children: ReactNode;
}

const RECONNECT_DELAY_INITIAL = 1000; // 1 seconde
const RECONNECT_DELAY_MAX = 30000; // 30 secondes
const RECONNECT_BACKOFF_MULTIPLIER = 1.5;

/** Active les traces WebSocket en dev uniquement (connexion, messages reçus) */
const WS_TRACE = import.meta.env.DEV;

export function U2000WebSocketProvider({ children }: U2000WebSocketProviderProps) {
  const { auth } = useU2000AuthContext();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectDelayRef = useRef<number>(RECONNECT_DELAY_INITIAL);
  const listenersRef = useRef<MessageListeners>({});
  const shouldReconnectRef = useRef<boolean>(true);
  const connectRef = useRef<() => void>(() => {});

  /**
   * Login "complet" (après login/refresh), pas seulement prelogin.
   * Évite 4401 "Utilisateur sans security tags" et connexions inutiles.
   */
  const isFullyAuthenticated =
    !!auth.isAuthenticated &&
    !!auth.token &&
    ((auth.securityTags?.length ?? 0) > 0 || !!(auth.user?.userName));

  /**
   * Notifie tous les listeners pour un type de message donné
   */
  const notifyListeners = useCallback((message: U2000WebSocketMessage) => {
    const listeners = listenersRef.current[message.type];
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          (listener as (m: U2000WebSocketMessage) => void)(message);
        } catch (error) {
          console.error(`[WebSocket] Error in listener for ${message.type}:`, error);
        }
      });
    }
  }, []);

  /**
   * Gère la réception d'un message WebSocket
   */
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (WS_TRACE) {
          console.log("[WebSocket] Message reçu:", data?.type ?? "(sans type)", data);
        }
        if (data && typeof data === "object" && "type" in data) {
          notifyListeners(data as U2000WebSocketMessage);
          if (WS_TRACE) {
            const count = listenersRef.current[data.type as keyof typeof listenersRef.current]?.size ?? 0;
            console.log("[WebSocket] Listeners notifiés:", data.type, `(${count} listener(s))`);
          }
        } else {
          console.warn("[WebSocket] Message reçu sans type:", data);
        }
      } catch (error) {
        console.error("[WebSocket] Erreur lors du parsing du message:", error, event.data);
      }
    },
    [notifyListeners]
  );

  /**
   * Gère la fermeture de la connexion WebSocket
   */
  const handleClose = useCallback(
    (event: CloseEvent) => {
      if (WS_TRACE) {
        console.log("[WebSocket] Connexion fermée:", { code: event.code, reason: event.reason, wasClean: event.wasClean });
      }
      setIsConnected(false);
      wsRef.current = null;

      // Code 4401 = Unauthorized (défini par le backend)
      // Ne pas reconnecter si auth invalide
      if (event.code === 4401) {
        console.warn("[WebSocket] Connexion fermée pour authentification invalide");
        shouldReconnectRef.current = false;
        return;
      }

      // Reconnecter uniquement si on doit reconnecter et login complet
      if (shouldReconnectRef.current && isFullyAuthenticated) {
        const delay = Math.min(reconnectDelayRef.current, RECONNECT_DELAY_MAX);
        if (WS_TRACE) {
          console.log(`[WebSocket] Reconnexion prévue dans ${delay}ms...`);
        }
        reconnectTimeoutRef.current = window.setTimeout(() => {
          reconnectDelayRef.current *= RECONNECT_BACKOFF_MULTIPLIER;
          connectRef.current();
        }, delay);
      }
    },
    [isFullyAuthenticated]
  );

  /**
   * Gère les erreurs WebSocket
   */
  const handleError = useCallback((error: Event) => {
    console.error("[WebSocket] Erreur:", error);
    // La fermeture sera gérée par handleClose
  }, []);

  /**
   * Établit la connexion WebSocket
   */
  const connect = useCallback(() => {
    // Ne pas connecter si déjà connecté ou si pas authentifié (login complet requis)
    if (wsRef.current?.readyState === WebSocket.OPEN || !isFullyAuthenticated) {
      return;
    }

    // Fermer la connexion existante si présente (BL-0435 : nuller handlers avant close)
    if (wsRef.current) {
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      const url = getWebSocketUrl(auth.token!);
      if (WS_TRACE) {
        console.log("[WebSocket] Connexion en cours:", url.replace(/token=[^&]+/, "token=***"));
      }

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectDelayRef.current = RECONNECT_DELAY_INITIAL; // Réinitialiser le délai en cas de succès
        if (WS_TRACE) {
          console.log("[WebSocket] Connexion établie (readyState=OPEN)");
        }
      };

      ws.onmessage = handleMessage;
      ws.onclose = handleClose;
      ws.onerror = handleError;
    } catch (error) {
      console.error("[WebSocket] Erreur lors de la création de la connexion:", error);
      setIsConnected(false);
    }
  }, [isFullyAuthenticated, auth.token, handleMessage, handleClose, handleError]);

  connectRef.current = connect;

  /**
   * Ferme la connexion WebSocket
   */
  const disconnect = useCallback(() => {
    if (WS_TRACE && wsRef.current) {
      console.log("[WebSocket] Déconnexion manuelle");
    }
    shouldReconnectRef.current = false;
    if (reconnectTimeoutRef.current !== null) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      // BL-0435 : éviter double livraison si l'ancienne socket reçoit encore après reconnect
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  /**
   * Se désabonner d'un type de message
   */
  const off = useCallback(<T extends U2000WebSocketMessage["type"]>(
    type: T,
    listener: (message: Extract<U2000WebSocketMessage, { type: T }>) => void
  ) => {
    const listeners = listenersRef.current[type];
    if (listeners) {
      listeners.delete(listener as MessageListener);
      if (listeners.size === 0) {
        delete listenersRef.current[type];
      }
      if (WS_TRACE) {
        console.log("[WebSocket] Listener retiré:", type, `(${listeners?.size ?? 0} restant(s))`);
      }
    }
  }, []);

  /**
   * S'abonner à un type de message
   */
  const on = useCallback(<T extends U2000WebSocketMessage["type"]>(
    type: T,
    listener: (message: Extract<U2000WebSocketMessage, { type: T }>) => void
  ) => {
    if (!listenersRef.current[type]) {
      listenersRef.current[type] = new Set();
    }
    listenersRef.current[type]!.add(listener as MessageListener);
    if (WS_TRACE) {
      console.log("[WebSocket] Listener ajouté:", type, `(${listenersRef.current[type]!.size} total)`);
    }

    // Retourner une fonction pour se désabonner
    return () => {
      off(type, listener as MessageListener);
    };
  }, [off]);

  // Se connecter uniquement après login complet (pas en prelogin), pour éviter 4401 "Utilisateur sans security tags"
  useEffect(() => {
    if (isFullyAuthenticated) {
      if (WS_TRACE) {
        console.log("[WebSocket] Auth OK (login complet), démarrage de la connexion...");
      }
      shouldReconnectRef.current = true;
      reconnectDelayRef.current = RECONNECT_DELAY_INITIAL;
      connect();
    } else {
      if (WS_TRACE) {
        console.log("[WebSocket] Non authentifié ou prelogin, déconnexion.");
      }
      disconnect();
    }

    return () => {
      if (WS_TRACE) {
        console.log("[WebSocket] Nettoyage (unmount / auth change)");
      }
      disconnect();
    };
  }, [isFullyAuthenticated, connect, disconnect]);

  // Se déconnecter au logout
  useEffect(() => {
    if (!auth.isAuthenticated) {
      disconnect();
    }
  }, [auth.isAuthenticated, disconnect]);

  const contextValue: U2000WebSocketContextType = {
    isConnected,
    on,
    off,
  };

  return <U2000WebSocketContext.Provider value={contextValue}>{children}</U2000WebSocketContext.Provider>;
}
